import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  TextField,
  Avatar,
  IconButton,
  Button,
  Menu,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Swal from 'sweetalert2';
import { notify } from '../../../common/functions/react-toastify.function';
import { getCurrentUserEmail } from '../../../common/functions/get_user_email_from_storage.function';
import {
  createInvoiceComment,
  getInvoiceComments,
  updateInvoiceComment,
  deleteInvoiceComment,
} from '../functions/invoice_comments.function';

const CommentsSection = forwardRef(
  (
    {
      invoiceId,
      commentByRoleId,
      commentByRoleName,
      onCommentsUpdated,
      onUnsavedChange,
      userType = 'admin',
    },
    ref
  ) => {
    const [comment, setComment] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [existingComments, setExistingComments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedCommentId, setSelectedCommentId] = useState(null);
    const [editingCommentId, setEditingCommentId] = useState(null);
    const [editCommentText, setEditCommentText] = useState('');

    // Get current user email from common function
    const currentUserEmail = getCurrentUserEmail();

    const getInitials = (userName, roleName) => {
      const name = userName || roleName;
      if (!name) {
        return 'U';
      }
      const words = name.trim().split(' ');
      if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    };

    const formatTimeAgo = (dateString) => {
      if (!dateString) {
        return '';
      }
      try {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);

        if (diffInSeconds < 60) {
          return 'Just now';
        }
        if (diffInSeconds < 3600) {
          return `${Math.floor(diffInSeconds / 60)}m ago`;
        }
        if (diffInSeconds < 86400) {
          return `${Math.floor(diffInSeconds / 3600)}h ago`;
        }
        if (diffInSeconds < 604800) {
          return `${Math.floor(diffInSeconds / 86400)}d ago`;
        }

        // Format as date if older than a week
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
        });
      } catch (e) {
        return '';
      }
    };

    const fetchComments = useCallback(async () => {
      if (!invoiceId) {
        return;
      }

      try {
        setLoading(true);
        const comments = await getInvoiceComments(invoiceId, userType);
        const sortedComments = (comments || []).sort((a, b) => {
          const dateA = new Date(a.comment_at || 0);
          const dateB = new Date(b.comment_at || 0);
          return dateB - dateA;
        });
        setExistingComments(sortedComments);
      } catch (error) {
        console.error('Error fetching comments:', error);
        setExistingComments([]);
      } finally {
        setLoading(false);
      }
    }, [invoiceId, userType]);

    useEffect(() => {
      fetchComments();
    }, [fetchComments]);

    const showButtons = isFocused || (comment && comment.trim().length > 0);

    const handleCancel = () => {
      setComment('');
      setIsFocused(false);
    };

    const hasUnsavedChanges = useCallback(() => {
      if (comment.trim().length > 0) {
        return true;
      }
      if (editingCommentId != null) {
        const original = existingComments.find((c) => c.id === editingCommentId);
        const originalText = (original?.comment || '').trim();
        const currentText = (editCommentText || '').trim();
        return currentText !== originalText;
      }
      return false;
    }, [comment, editingCommentId, editCommentText, existingComments]);

    useEffect(() => {
      if (onUnsavedChange) {
        onUnsavedChange(hasUnsavedChanges());
      }
    }, [hasUnsavedChanges, onUnsavedChange]);

    const handleSave = async () => {
      if (!comment.trim()) {
        notify('error', 'Please fill in all required fields');
        return false;
      }

      try {
        setSaving(true);
        const commentData = {
          invoice_id: invoiceId,
          comment: comment.trim(),
          comment_by_roleId: commentByRoleId,
          comment_by_roleName: commentByRoleName,
          comment_at: new Date().toISOString(),
          is_edited: false,
        };

        const response = await createInvoiceComment(commentData, userType);
        // Use the response directly instead of refetching
        // API returns { status, message, response: {...} } or direct object
        const newComment = response?.response || response;
        if (newComment) {
          // Ensure comment_by_roleName is included in the comment object
          const commentWithRoleName = {
            ...newComment,
            comment_by_roleName: newComment.comment_by_roleName || commentByRoleName,
          };
          // Add the new comment to the beginning of the list
          setExistingComments((prev) => [commentWithRoleName, ...prev]);
        }
        notify('success', 'Comment added successfully');
        setComment('');
        setIsFocused(false);
        if (onCommentsUpdated) {
          onCommentsUpdated();
        }
        return true;
      } catch (error) {
        console.error('Error creating comment:', error);
        return false;
      } finally {
        setSaving(false);
      }
    };

    // Handle three dots menu
    const handleMenuOpen = (event, commentId) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
      setSelectedCommentId(commentId);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
      setSelectedCommentId(null);
    };

    // Handle edit comment
    const handleEditClick = () => {
      const commentToEdit = existingComments.find((c) => c.id === selectedCommentId);
      if (commentToEdit) {
        // Check if user owns this comment by email
        if (!currentUserEmail || commentToEdit.comment_by_userEmail !== currentUserEmail) {
          notify('error', 'You can only edit your own comments');
          handleMenuClose();
          return;
        }
        setEditingCommentId(selectedCommentId);
        setEditCommentText(commentToEdit.comment);
      }
      handleMenuClose();
    };

    // Handle save edited comment
    const handleSaveEdit = async () => {
      if (!editCommentText.trim() || !editingCommentId) {
        return false;
      }

      // Check if user owns this comment by email before saving
      const commentToUpdate = existingComments.find((c) => c.id === editingCommentId);
      if (
        !currentUserEmail ||
        !commentToUpdate ||
        commentToUpdate.comment_by_userEmail !== currentUserEmail
      ) {
        notify('error', 'You can only edit your own comments');
        setEditingCommentId(null);
        setEditCommentText('');
        return false;
      }

      try {
        setSaving(true);
        const response = await updateInvoiceComment(
          editingCommentId,
          {
            comment: editCommentText.trim(),
          },
          userType
        );
        // Use the response directly instead of refetching
        // API returns { status, message, response: {...} } or direct object
        const updatedComment = response?.response || response;
        if (updatedComment) {
          // Update the comment in the list
          setExistingComments((prev) =>
            prev.map((comment) =>
              comment.id === editingCommentId ? { ...comment, ...updatedComment } : comment
            )
          );
        }
        notify('success', 'Comment updated successfully');
        setEditingCommentId(null);
        setEditCommentText('');
        if (onCommentsUpdated) {
          onCommentsUpdated();
        }
        return true;
      } catch (error) {
        console.error('Error updating comment:', error);
        return false;
      } finally {
        setSaving(false);
      }
    };

    const saveDraft = useCallback(async () => {
      if (!hasUnsavedChanges()) {
        return true;
      }
      if (editingCommentId != null) {
        return handleSaveEdit();
      }
      if (comment.trim()) {
        return handleSave();
      }
      return true;
    }, [hasUnsavedChanges, editingCommentId, comment, handleSaveEdit, handleSave]);

    useImperativeHandle(
      ref,
      () => ({
        hasUnsavedChanges,
        saveDraft,
      }),
      [hasUnsavedChanges, saveDraft]
    );

    // Handle cancel edit
    const handleCancelEdit = () => {
      setEditingCommentId(null);
      setEditCommentText('');
    };

    // Handle delete comment
    const handleDeleteClick = async () => {
      if (!selectedCommentId) {
        return;
      }

      // Check if user owns this comment by email before showing confirmation
      const commentToDelete = existingComments.find((c) => c.id === selectedCommentId);
      if (
        !currentUserEmail ||
        !commentToDelete ||
        commentToDelete.comment_by_userEmail !== currentUserEmail
      ) {
        notify('error', 'You can only delete your own comments');
        handleMenuClose();
        return;
      }

      handleMenuClose();

      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'Are you sure you want to delete this comment?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#BD1D23',
        cancelButtonColor: '#667085',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel',
        customClass: {
          popup: 'small-swal',
        },
      });

      if (!result.isConfirmed) {
        return;
      }

      // Double-check ownership by email before deleting
      const commentToDeleteAfterConfirm = existingComments.find((c) => c.id === selectedCommentId);
      if (
        !currentUserEmail ||
        !commentToDeleteAfterConfirm ||
        commentToDeleteAfterConfirm.comment_by_userEmail !== currentUserEmail
      ) {
        notify('error', 'You can only delete your own comments');
        return;
      }

      try {
        setSaving(true);
        await deleteInvoiceComment(selectedCommentId, userType);
        notify('success', 'Comment deleted successfully');
        // Remove the comment from the list directly
        setExistingComments((prev) => prev.filter((comment) => comment.id !== selectedCommentId));
        if (onCommentsUpdated) {
          onCommentsUpdated();
        }
      } catch (error) {
        console.error('Error deleting comment:', error);
      } finally {
        setSaving(false);
      }
    };

    // Check if comment belongs to current user by matching email
    const isCommentOwner = (comment) => {
      if (!currentUserEmail) {
        return false;
      }
      // Match comment_by_userEmail with current user's email from localStorage/sessionStorage
      return comment.comment_by_userEmail === currentUserEmail;
    };

    return (
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontSize: '16px',
            fontFamily: 'Inter',
            fontWeight: 600,
            mb: 2,
            color: '#192126',
            display: 'flex',
          }}
        >
          Add Comments:
        </Typography>
        <TextField
          multiline
          rows={4}
          placeholder="Add comment..."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={saving}
          sx={{
            width: '100%',
            mb: showButtons ? 2 : 2,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#E8E9E9',
              },
              '&:hover fieldset': {
                borderColor: '#BD1D23',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#BD1D23',
              },
            },
          }}
        />

        {/* Cancel and Save Buttons */}
        {showButtons && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              onMouseDown={(e) => e.preventDefault()}
              disabled={saving}
              sx={{
                borderColor: '#E8E9E9',
                color: '#192126',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                '&:hover': {
                  borderColor: '#BD1D23',
                  backgroundColor: 'rgba(189, 29, 35, 0.04)',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              onMouseDown={(e) => e.preventDefault()}
              disabled={saving || !comment.trim()}
              sx={{
                backgroundColor: '#BD1D23',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: '#8B1519',
                },
              }}
            >
              {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
            </Button>
          </Box>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {/* Existing Comments - Scrollable Container */}
        {!loading && existingComments.length > 0 && (
          <Box
            sx={{
              maxHeight: '360px', // Approximately 3 comments height
              overflowY: 'auto',
              overflowX: 'hidden',
              pr: 1, // Padding for scrollbar
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '10px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '10px',
                '&:hover': {
                  background: '#555',
                },
              },
            }}
          >
            {existingComments.map((existingComment) => {
              const isEditing = editingCommentId === existingComment.id;
              const isOwner = isCommentOwner(existingComment);

              return (
                <Box
                  key={existingComment.id}
                  sx={{
                    display: 'flex',
                    gap: 2,
                    p: 2,
                    backgroundColor: '#F8F9FA',
                    borderRadius: '8px',
                    mb: 2,
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: '#BD1D23',
                      color: '#FFF',
                      width: 32,
                      height: 32,
                      fontSize: '12px',
                      fontWeight: 600,
                    }}
                  >
                    {getInitials(
                      existingComment.comment_by_userName,
                      existingComment.comment_by_roleName
                    )}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    {isEditing ? (
                      <>
                        <TextField
                          multiline
                          rows={3}
                          value={editCommentText}
                          onChange={(e) => setEditCommentText(e.target.value)}
                          fullWidth
                          sx={{
                            mb: 1,
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: '#E8E9E9',
                              },
                              '&:hover fieldset': {
                                borderColor: '#BD1D23',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#BD1D23',
                              },
                            },
                          }}
                        />
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            onClick={handleCancelEdit}
                            disabled={saving}
                            sx={{ textTransform: 'none' }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleSaveEdit}
                            disabled={saving || !editCommentText.trim()}
                            sx={{
                              backgroundColor: '#BD1D23',
                              textTransform: 'none',
                              '&:hover': {
                                backgroundColor: '#8B1519',
                              },
                            }}
                          >
                            {saving ? <CircularProgress size={16} color="inherit" /> : 'Save'}
                          </Button>
                        </Box>
                      </>
                    ) : (
                      <>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 0.5,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '14px',
                              fontFamily: 'Inter',
                              fontWeight: 600,
                              color: '#192126',
                            }}
                          >
                            {existingComment.comment_by_userName ||
                              existingComment.comment_by_roleName ||
                              'Unknown'}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '14px',
                              fontFamily: 'Inter',
                              fontWeight: 400,
                              color: '#667085',
                            }}
                          >
                            ({existingComment.comment_by_roleName || 'Admin'})
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '12px',
                              fontFamily: 'Inter',
                              fontWeight: 400,
                              color: '#667085',
                            }}
                          >
                            {formatTimeAgo(existingComment.comment_at)}
                          </Typography>
                          {existingComment.is_edited && (
                            <Typography
                              sx={{
                                fontSize: '11px',
                                fontFamily: 'Inter',
                                fontWeight: 400,
                                color: '#667085',
                                fontStyle: 'italic',
                              }}
                            >
                              (edited)
                            </Typography>
                          )}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontFamily: 'Inter',
                            fontWeight: 400,
                            color: '#192126',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {existingComment.comment}
                        </Typography>
                      </>
                    )}
                  </Box>
                  {!isEditing && isOwner && (
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, existingComment.id)}
                      sx={{
                        width: 32,
                        height: 32,
                        padding: 0,
                        borderRadius: '50%',
                        color: '#667085',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        {/* Three Dots Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleEditClick}>
            <EditIcon sx={{ mr: 1, fontSize: 18 }} />
            Edit
          </MenuItem>
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1, fontSize: 18 }} />
            Delete
          </MenuItem>
        </Menu>
      </Box>
    );
  }
);

CommentsSection.displayName = 'CommentsSection';

export default CommentsSection;
